import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import {
  DeleteSituReelRequest,
  GetSituReelRequest,
  ListSituReelsRequest,
  ProductName,
  SituReel as ApiSituReel,
  SituReelItem as ApiSituReelItem,
  SituReelPublic,
  SituReelPublicItem,
  UpsertSituReelItem,
  UpsertSituReelRequest
} from 'situview-webapi-client';
import { CustomSituViewWebApiClient } from './custom-situ-view-web-api-client.service';

export interface SituReelItem {
  artifactId: string;
  thumbnailArtifactId?: string;
  label: string;
  subtitle?: string;
  displayOrder: number;
  productName?: ProductName;
  thumbnailOverrideUrl?: string;
  // Bare query string fragment, no leading '?'. Only meaningful for
  // SituView-family items (Panorama / RayTraced / RayTracedPlus). Parsed
  // and merged into route params by the factory dispatcher.
  startViewQuery?: string;
}

export interface SituReel {
  id: string;
  name: string;
  description?: string;
  defaultArtifactId: string;
  items: SituReelItem[];
}

@Injectable({
  providedIn: 'root'
})
export class SituReelService {
  private readonly reelsChangedSubject = new BehaviorSubject<SituReel[]>([]);
  readonly reelsChanged$: Observable<SituReel[]> = this.reelsChangedSubject.asObservable();

  constructor(private apiClient: CustomSituViewWebApiClient) {}

  async getReel(reelId: string): Promise<SituReel | null> {
    if (!reelId) return null;
    const request = new GetSituReelRequest({ id: reelId });
    try {
      const response = await this.apiClient.getSituReel(request);
      if (!response?.success || !response.situReel) return null;
      return this.fromApi(response.situReel);
    } catch {
      return null;
    }
  }

  async listReels(companyId: string): Promise<SituReel[]> {
    if (!companyId) return [];
    const request = new ListSituReelsRequest({ companyId, includeInactive: false });
    const response = await this.apiClient.listSituReels(request);
    const reels = (response?.situReels ?? [])
      .map(r => this.fromApi(r))
      .sort((a, b) => a.name.localeCompare(b.name));
    this.reelsChangedSubject.next(reels);
    return reels;
  }

  async upsertReel(reel: SituReel, companyId: string): Promise<SituReel> {
    const request = new UpsertSituReelRequest({
      id: reel.id || undefined,
      companyId,
      name: reel.name,
      description: reel.description,
      defaultArtifactId: reel.defaultArtifactId,
      items: [...reel.items]
        .sort((a, b) => a.displayOrder - b.displayOrder)
        .map((item, idx) => new UpsertSituReelItem({
          artifactId: item.artifactId,
          thumbnailArtifactId: item.thumbnailArtifactId,
          label: item.label,
          subtitle: item.subtitle,
          productName: item.productName,
          thumbnailOverrideUrl: item.thumbnailOverrideUrl,
          startViewQuery: item.startViewQuery,
          displayOrder: idx
        }))
    });

    const response = await this.apiClient.upsertSituReel(request);
    if (!response?.success || !response.situReel) {
      throw new Error(response?.errorMessages?.join(', ') || 'Unable to save SituReel');
    }
    await this.listReels(companyId);
    return this.fromApi(response.situReel);
  }

  async deleteReel(reelId: string, companyId: string): Promise<void> {
    if (!reelId) return;
    const request = new DeleteSituReelRequest({ id: reelId, companyId });
    const response = await this.apiClient.deleteSituReel(request);
    if (!response?.success) {
      throw new Error(response?.errorMessages?.join(', ') || 'Unable to delete SituReel');
    }
    await this.listReels(companyId);
  }

  private fromApi(api: ApiSituReel | SituReelPublic): SituReel {
    return {
      id: api.id ?? '',
      name: api.name ?? '',
      description: api.description,
      defaultArtifactId: api.defaultArtifactId ?? '',
      items: (api.items ?? [])
        .slice()
        .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))
        .map(i => this.itemFromApi(i))
    };
  }

  private itemFromApi(api: ApiSituReelItem | SituReelPublicItem): SituReelItem {
    return {
      artifactId: api.artifactId ?? '',
      thumbnailArtifactId: api.thumbnailArtifactId,
      label: api.label ?? '',
      subtitle: api.subtitle,
      displayOrder: api.displayOrder ?? 0,
      productName: api.productName,
      thumbnailOverrideUrl: api.thumbnailOverrideUrl,
      startViewQuery: api.startViewQuery
    };
  }
}
